import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, Home, BarChart3 } from 'lucide-react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../../../../styles/datepicker-custom.css';

import { useTranslation } from 'react-i18next';
import { useAuth, useOrgaConfigurationContext } from '@/app/providers';
import { getOrgStatsDailyRange } from '@/services/firebase/org-stats-repository';
import { countCustomerOrgs, countInactiveCustomerOrgs } from '@/services/firebase/customer-orgs-repository';
import { Card, CardBody, Badge } from '@/components/ui';
import { useTheme } from '@/shared/contexts/ThemeContext';

import { AnalyticsData } from './types';
import { aggregateOrgStatsToAnalytics } from './aggregateOrgStatsToAnalytics';
import { getChartTheme } from './chartUtils';
import { BusinessSection } from './BusinessSection';
import { PaymentSection } from './PaymentSection';
import { DeliverySection } from './DeliverySection';
import { CustomerSection } from './CustomerSection';
import { LiveSection } from './LiveSection';
import { TimingSection } from './TimingSection';

/** YYYY-MM-DD en heure locale (évite décalage UTC vs orgStatsDaily.day). */
function toYYYYMMDD(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

/**
 * Dashboard Analytics Vendeur
 * Affiche les KPIs business, paiement, livraison, clients et live
 */
const AnalyticsDashboard: React.FC = () => {
    const { idorga } = useParams<{ idorga: string }>();
    const { currentUser } = useAuth();
    const { configuration } = useOrgaConfigurationContext();
    const { t } = useTranslation();
    const { theme: appTheme } = useTheme();

    /** orgStatsDaily utilise orgId = slug (ex. "paypal"). Priorité config.organizationId > idorga. */
    const orgIdForStats = configuration?.organizationId ?? idorga ?? '';

    // State
    const [loading, setLoading] = useState(true);
    const [activeSection, setActiveSection] = useState<string>('business');

    // Date range - par défaut ce mois
    const [startDate, setStartDate] = useState<Date>(() => {
        const date = new Date();
        date.setDate(1);
        date.setHours(0, 0, 0, 0);
        return date;
    });
    const [endDate, setEndDate] = useState<Date>(() => {
        const date = new Date();
        date.setHours(23, 59, 59, 999);
        return date;
    });
    const [selectedPeriod, setSelectedPeriod] = useState<string>('thisMonth');

    // Theme
    const theme = useMemo(() => getChartTheme(appTheme), [appTheme]);

    // Load org stats (no order scan) – orgId = config.organizationId pour matcher orgStatsDaily
    const [orgStatsDocs, setOrgStatsDocs] = useState<Awaited<ReturnType<typeof getOrgStatsDailyRange>>>([]);
    // Inactifs / total clients (source: customerOrgs, cohérent avec la page Clients)
    const [inactiveCount, setInactiveCount] = useState<number>(0);
    const [totalCustomersFromOrgs, setTotalCustomersFromOrgs] = useState<number>(0);
    useEffect(() => {
        if (!currentUser || !orgIdForStats) {
            setLoading(false);
            setOrgStatsDocs([]);
            return;
        }
        setLoading(true);
        const startDay = toYYYYMMDD(startDate);
        const endDay = toYYYYMMDD(endDate);
        getOrgStatsDailyRange(orgIdForStats, startDay, endDay)
            .then((docs) => {
                setOrgStatsDocs(docs);
                if (process.env.NODE_ENV === 'development') {
                    console.log(`📊 [Analytics] orgStatsDaily queried orgId=${orgIdForStats} ${startDay}→${endDay} → ${docs.length} doc(s)`);
                }
                setLoading(false);
            })
            .catch((err) => {
                console.warn('[Analytics] getOrgStatsDailyRange failed:', orgIdForStats, startDay, endDay, err);
                setOrgStatsDocs([]);
                setLoading(false);
            });
    }, [currentUser, orgIdForStats, startDate, endDate]);

    // Charger total clients et inactifs (customerOrgs) pour la section Clients
    useEffect(() => {
        if (!orgIdForStats) return;
        const periodStart = new Date(startDate);
        periodStart.setHours(0, 0, 0, 0);
        Promise.all([
            countCustomerOrgs(orgIdForStats),
            countInactiveCustomerOrgs(orgIdForStats, periodStart),
        ])
            .then(([total, inactive]) => {
                setTotalCustomersFromOrgs(total);
                setInactiveCount(inactive);
            })
            .catch((err) => {
                console.warn('[Analytics] countCustomerOrgs/countInactiveCustomerOrgs failed:', err);
                setTotalCustomersFromOrgs(0);
                setInactiveCount(0);
            });
    }, [orgIdForStats, startDate]);

    // Aggregate org stats → AnalyticsData
    const analyticsData = useMemo<AnalyticsData | null>(() => {
        const startDay = toYYYYMMDD(startDate);
        const endDay = toYYYYMMDD(endDate);
        return aggregateOrgStatsToAnalytics(orgStatsDocs, startDay, endDay);
    }, [orgStatsDocs, startDate, endDate]);

    // Period selection handler
    const handlePeriodSelect = (period: string) => {
        setSelectedPeriod(period);
        const today = new Date();
        const start = new Date();

        switch (period) {
            case 'today':
                start.setHours(0, 0, 0, 0);
                setStartDate(start);
                setEndDate(new Date(today.setHours(23, 59, 59, 999)));
                break;
            case 'yesterday':
                start.setDate(today.getDate() - 1);
                start.setHours(0, 0, 0, 0);
                const endYesterday = new Date(start);
                endYesterday.setHours(23, 59, 59, 999);
                setStartDate(start);
                setEndDate(endYesterday);
                break;
            case 'last7days':
                start.setDate(today.getDate() - 7);
                start.setHours(0, 0, 0, 0);
                setStartDate(start);
                setEndDate(new Date(new Date().setHours(23, 59, 59, 999)));
                break;
            case 'last30days':
                start.setDate(today.getDate() - 30);
                start.setHours(0, 0, 0, 0);
                setStartDate(start);
                setEndDate(new Date(new Date().setHours(23, 59, 59, 999)));
                break;
            case 'thisWeek':
                const dayOfWeek = today.getDay();
                const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
                start.setDate(today.getDate() + diffToMonday);
                start.setHours(0, 0, 0, 0);
                setStartDate(start);
                setEndDate(new Date(new Date().setHours(23, 59, 59, 999)));
                break;
            case 'thisMonth':
                start.setDate(1);
                start.setHours(0, 0, 0, 0);
                setStartDate(start);
                setEndDate(new Date(new Date().setHours(23, 59, 59, 999)));
                break;
            case 'lastMonth':
                const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                const lastDayOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
                lastMonth.setHours(0, 0, 0, 0);
                lastDayOfLastMonth.setHours(23, 59, 59, 999);
                setStartDate(lastMonth);
                setEndDate(lastDayOfLastMonth);
                break;
        }
    };

    const handleDateRangeChange = (dates: [Date | null, Date | null]) => {
        const [start, end] = dates;
        if (start) {
            const normalizedStart = new Date(start);
            normalizedStart.setHours(0, 0, 0, 0);
            setStartDate(normalizedStart);
        }
        if (end) {
            const normalizedEnd = new Date(end);
            normalizedEnd.setHours(23, 59, 59, 999);
            setEndDate(normalizedEnd);
        }
    };

    // Note: handleRefresh removed as it's real-time with Firestore

    // Navigation items
    const sections = [
        { id: 'business', label: t('admin.dashboard.analytics.sections.business'), icon: '💰' },
        { id: 'payment', label: t('admin.dashboard.analytics.sections.payment'), icon: '💳' },
        { id: 'delivery', label: t('admin.dashboard.analytics.sections.delivery'), icon: '📦' },
        { id: 'customer', label: t('admin.dashboard.analytics.sections.customer'), icon: '👤' },
        { id: 'timing', label: t('admin.dashboard.analytics.sections.timing'), icon: '⏰' },
        { id: 'live', label: t('admin.dashboard.analytics.sections.live'), icon: '🎥' },
    ];

    // Period buttons
    const periods = [
        { id: 'today', label: t('admin.dashboard.analytics.periods.today') },
        { id: 'yesterday', label: t('admin.dashboard.analytics.periods.yesterday') },
        { id: 'last7days', label: t('admin.dashboard.analytics.periods.last7days') },
        { id: 'thisWeek', label: t('admin.dashboard.analytics.periods.thisWeek') },
        { id: 'thisMonth', label: t('admin.dashboard.analytics.periods.thisMonth') },
        { id: 'lastMonth', label: t('admin.dashboard.analytics.periods.lastMonth') },
        { id: 'last30days', label: t('admin.dashboard.analytics.periods.last30days') },
    ];

    // Custom date picker input
    const CustomDatePickerInput = React.forwardRef<HTMLDivElement, any>(
        ({ value, onClick }, ref) => {
            let displayValue = value;
            if (value && value.includes(' - ')) {
                const [start, end] = value.split(' - ');
                if (start === end) displayValue = start;
            }
            return (
                <div ref={ref} className="relative">
                    <input
                        type="text"
                        value={displayValue}
                        onClick={onClick}
                        readOnly
                        placeholder={t('admin.dashboard.analytics.periods.selectPeriod')}
                        className="w-full px-3 py-2 pr-10 border border-border rounded-md text-sm cursor-pointer text-foreground bg-surface focus:outline-none focus:ring-2 focus:ring-primary-light"
                    />
                </div>
            );
        }
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
                    <p className="text-foreground-secondary">{t('admin.dashboard.analytics.loading')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background-secondary text-foreground pb-10">
            <div className="bg-surface border-b border-border shadow-sm">
                <div className="pt-4 sm:pt-6 pb-4 px-4 sm:px-6">
                    <nav className="flex items-center justify-end flex-wrap gap-2 sm:gap-3 text-sm">
                        <Link
                            to="/organizations/manage"
                            className="flex items-center space-x-2 px-3 py-2 rounded-lg text-primary hover:text-primary-dark hover:bg-primary-20 transition-all duration-200 font-medium group"
                        >
                            <ChevronLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                            <Home className="w-4 h-4" />
                            <span>{t('admin.configuration.breadcrumb.organizations')}</span>
                        </Link>
                        <span className="text-foreground-secondary">/</span>
                        <span className="text-foreground font-medium">
                            {t('admin.dashboard.analytics.title')}
                        </span>
                    </nav>
                </div>
            </div>

            {/* Header - New Style Title Box */}
            <Card variant="default" className="mt-6 mx-2 sm:mx-4 lg:ml-2.5 lg:mr-6">
                <CardBody className="px-4 sm:px-6 py-4 sm:py-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div>
                            <div className="flex items-center gap-3">
                                <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
                                    {t('admin.dashboard.analytics.title')}
                                </h1>
                                <Badge variant="success" size="sm" className="border-0">
                                    <span className="relative flex h-2 w-2 mr-1">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75"></span>
                                        <span className="relative inline-flex rounded-full h-2 w-2 bg-success"></span>
                                    </span>
                                    {t('admin.dashboard.analytics.realTime')}
                                </Badge>
                            </div>
                            <p className="mt-2 text-sm text-foreground-secondary font-medium">
                                {t('admin.dashboard.analytics.subtitle')}
                            </p>
                        </div>
                    </div>
                </CardBody>
            </Card>

            {/* Contenu principal avec les mêmes marges */}
            <div className="mt-6 mx-2 sm:mx-4 lg:ml-2.5 lg:mr-6 space-y-6">
                {/* Filters */}
                <Card>
                    <CardBody>
                        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                            {/* Period Quick Buttons */}
                            <div className="flex flex-wrap gap-2">
                                {periods.map((period) => (
                                    <button
                                        key={period.id}
                                        onClick={() => handlePeriodSelect(period.id)}
                                        className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all ${selectedPeriod === period.id
                                            ? 'bg-primary text-white'
                                            : 'bg-background-tertiary text-foreground-secondary hover:bg-primary-10 hover:text-primary'
                                            }`}
                                    >
                                        {period.label}
                                    </button>
                                ))}
                            </div>

                            {/* Date Picker */}
                            <div className="flex-1 max-w-xs">
                                <ReactDatePicker
                                    selectsRange
                                    startDate={startDate}
                                    endDate={endDate}
                                    onChange={handleDateRangeChange}
                                    dateFormat="dd/MM/yyyy"
                                    customInput={<CustomDatePickerInput />}
                                    isClearable={false}
                                    monthsShown={1}
                                    calendarStartDay={1}
                                />
                            </div>
                        </div>
                    </CardBody>
                </Card>

                {/* Section Navigation */}
                <div className="flex overflow-x-auto gap-2 mb-6 pb-2">
                    {sections.map((section) => (
                        <button
                            key={section.id}
                            onClick={() => setActiveSection(section.id)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-all ${activeSection === section.id
                                ? 'bg-primary text-white shadow-lg'
                                : 'bg-surface border border-border text-foreground-secondary hover:bg-background-tertiary'
                                }`}
                        >
                            <span>{section.icon}</span>
                            <span className="font-medium">{section.label}</span>
                        </button>
                    ))}
                </div>

                {/* Content */}
                {!loading && orgStatsDocs.length > 0 && analyticsData ? (
                    <div className="space-y-8">
                        {activeSection === 'business' && (
                            <BusinessSection data={analyticsData.business} theme={theme} />
                        )}
                        {activeSection === 'payment' && (
                            <PaymentSection data={analyticsData.payment} theme={theme} />
                        )}
                        {activeSection === 'delivery' && (
                            <DeliverySection data={analyticsData.delivery} theme={theme} />
                        )}
                        {activeSection === 'customer' && (
                            <CustomerSection
                                customerData={analyticsData.customer}
                                theme={theme}
                                period={selectedPeriod === 'last7days' ? 7 : selectedPeriod === 'last30days' || selectedPeriod === 'thisMonth' ? 30 : 90}
                                inactiveCount={inactiveCount}
                                totalCustomersFromOrgs={totalCustomersFromOrgs}
                            />
                        )}
                        {activeSection === 'timing' && (
                            <TimingSection data={analyticsData.timing} theme={theme} />
                        )}
                        {activeSection === 'live' && (
                            <LiveSection data={analyticsData.live} theme={theme} />
                        )}
                    </div>
                ) : !loading ? (
                    <Card>
                        <CardBody>
                            <div className="text-center py-12">
                                <BarChart3 className="h-16 w-16 text-foreground-secondary mx-auto mb-4 opacity-50" />
                                <h3 className="text-lg font-semibold text-foreground mb-2">
                                    {t('admin.dashboard.analytics.noData')}
                                </h3>
                                <p className="text-foreground-secondary">
                                    {t('admin.dashboard.analytics.noDataDesc')}
                                </p>
                            </div>
                        </CardBody>
                    </Card>
                ) : null}
            </div>
        </div>
    );
};

export default AnalyticsDashboard;
